import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Typography,
  Col,
  Row,
  Button,
  Dropdown,
  Menu,
} from "antd";
import { journalsQueries, DocumentFileRow, TitlePageFillersDropdown } from "@/entities/journal";
import { $journalApi } from "@/entities/journal/fetch/fetch-client";
import { useValidateTitle } from "@/features/journal-title-validation";
import { RepresentationDocumentationExaminationModal } from "@/features/representation-documentation-examination-modal";
import { BasketIcon, MoreOneIcon } from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import { useCoordinator, useDisclosure } from "@/shared/lib/hooks";
import { queryClient } from "@/shared/lib/react-query";
import {
  fallbackString,
  formatDate,
  useIsUnifiedMode,
} from "@/shared/utils";
import type { components } from "@entities/journal";
import { CollapsibleSection } from "../collapsible-section";
import { createValidationErrorsGetter } from "../journals-title/model/get-validation-errors-for-section";

type Examination = components["schemas"]["DocumentationExamination"];

interface IDocumentationExaminationTableNew {
  isViewMode?: boolean;
}

export const DocumentationExaminationTableNew = ({ isViewMode = false }: IDocumentationExaminationTableNew) => {
  const isUnifiedMode = useIsUnifiedMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { titleId } = useCoordinator();
  const { data, isFetching } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const { errorList } = useValidateTitle();

  const validationErrors = useMemo(() => {
    return errorList.filter((error) => error.id === 998);
  }, [errorList]);

  const getValidationErrorsForSection = useMemo(
    () => createValidationErrorsGetter(errorList),
    [errorList],
  );

  const titleStatus =
    data?.mainJournal?.titleJournal?.status === "NEW" &&
    // @ts-expect-error versionNumber отсутствует в openapi типе TitleJournalViewDto
    !data?.mainJournal?.titleJournal?.versionNumber;

  const {
    mutate: updateDocumentationExamination,
    isPending: isUpdatingDocumentationExamination,
  } = $journalApi.useMutation("put", "/api/v1/journals/title/{titleId}/docs", {
    body: (data: any) => data,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
      });
    },
  });

  const {
    mutate: deleteDocumentationExamination,
    isPending: isDeletingDocumentationExamination,
  } = $journalApi.useMutation(
    "delete",
    "/api/v1/journals/title/{titleId}/docs",
    {
      body: (data: any) => data,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
        });
      },
    },
  );

  const handleDelete = (examination: Examination) => {
    deleteDocumentationExamination({
      params: {
        path: { titleId },
      },
      body: {
        documentationExamination: {
          documentationExaminationId: examination.documentationExaminationId,
        },
      },
    });
  };

  const handleSelect = (examination: Examination) => {
    onClose();
    updateDocumentationExamination({
      params: {
        path: { titleId },
      },
      body: {
        documentationExamination: {
          documentationExaminationId: examination.documentationExaminationId,
          selectedDocumentExamination: true,
        },
      },
    });
  };

  const expertise =
    data?.mainJournal?.titleJournal?.documentation?.documentationExamination;

  const activeExamination = expertise?.find(
    (p) => p.selectedDocumentExamination,
  );

  const hasMultipleExaminations = (expertise?.length ?? 0) > 1;

  if (isUpdatingDocumentationExamination || isFetching) {
    return (
      <CollapsibleSection
        title="Сведения о положительном заключении экспертизы проектной документации"
        spinning
        validationErrors={validationErrors}
        sectionId="documentation-examination"
        getValidationErrorsForSection={getValidationErrorsForSection}
        isEmpty={!activeExamination}
      >
        <></>
      </CollapsibleSection>
    );
  }

  if (
    // @ts-expect-error expertise отсутствует в openapi типе documentation
    expertise?.length > 0 &&
    !activeExamination &&
    !isDeletingDocumentationExamination
  ) {
    return (
      <CollapsibleSection
        title="Сведения о положительном заключении экспертизы проектной документации"
        spinning={false}
        validationErrors={validationErrors}
        sectionId="documentation-examination"
        getValidationErrorsForSection={getValidationErrorsForSection}
        isEmpty={!activeExamination}
      >
        <>
          {titleStatus ? (
            <Can I="edit" a="title_journals">
              <Button
                type="primary"
                style={{ marginTop: "14px" }}
                onClick={onOpen}
              >
                Выбрать
              </Button>
              <RepresentationDocumentationExaminationModal
                open={isOpen}
                onClose={onClose}
                onSelect={handleSelect}
                // @ts-expect-error examination не совпадает с типом пропса модалки
                examination={expertise}
                title="Выберите положительное заключение экспертизы проектной документации"
              />
            </Can>
          ) : null}
        </>
      </CollapsibleSection>
    );
  }

  if (activeExamination) {
    return (
      <CollapsibleSection
        title="Сведения о положительном заключении экспертизы проектной документации"
        spinning={false}
        validationErrors={validationErrors}
        sectionId="documentation-examination"
        getValidationErrorsForSection={getValidationErrorsForSection}
        isEmpty={false}
      >
        <>
          <div className="journal-title-section-header journal-title-section-header--tight-top">
            <Typography.Title level={5} style={{ fontWeight: "400" }}>
              Заключение № {fallbackString(activeExamination.number)}
            </Typography.Title>
            {hasMultipleExaminations && titleStatus ? (
              <Can I="edit" a="title_journals">
                <Dropdown
                  overlay={
                    <Menu>
                      <Menu.Item
                        key="delete"
                        icon={<BasketIcon />}
                        onClick={() => handleDelete(activeExamination)}
                      >
                        Удалить
                      </Menu.Item>
                    </Menu>
                  }
                  trigger={["click"]}
                >
                  <div className="journal-title-menu-trigger">
                    <MoreOneIcon width={24} height={24} />
                  </div>
                </Dropdown>
              </Can>
            ) : null}
          </div>

          <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
            <Col span={8} style={{ color: "#888888" }}>
              Дата выдачи
            </Col>
            <Col span={16}>
              {fallbackString(formatDate(activeExamination.date))}
            </Col>

            <Col span={8} style={{ color: "#888888" }}>
              Орган или организация, его утвердившие
            </Col>
            <Col span={16}>{fallbackString(activeExamination.issued)}</Col>

            {activeExamination.examinationFile?.file && (
              <>
                <Col span={8} style={{ color: "#888888" }}>
                  Файл заключения
                </Col>
                <Col span={16} style={{ display: "flex", flexDirection: "row" }}>
                  {activeExamination.examinationFile.file.fileName ? (
                    <DocumentFileRow
                      inline
                      variant="plain"
                      downloadIconPosition="right"
                      fileName={activeExamination.examinationFile.file.fileName}
                      fileId={activeExamination.examinationFile.file.fileId}
                      disableLinkAction={isUnifiedMode}
                    />
                  ) : (
                    "-"
                  )}
                </Col>
              </>
            )}
          </Row>
        </>
      </CollapsibleSection>
    );
  }

  return (
    <CollapsibleSection
      title="Сведения о положительном заключении экспертизы проектной документации"
      spinning={false}
      validationErrors={validationErrors}
      sectionId="documentation-examination"
      getValidationErrorsForSection={getValidationErrorsForSection}
      isEmpty
    >
      {isViewMode ? (
        <div className="journal-empty-text">
          Данные не были заполнены
        </div>
      ) : (
        <div className="journal-empty-text">
          В карточке объекта указано, что сведения о положительном заключении
          экспертизы проектной документации не требуются Обратитесь к{" "}
          <TitlePageFillersDropdown text="администратору проекта" />{" "}
          если требуется изменить настройку.
        </div>
      )}
    </CollapsibleSection>
  );
};

